import React, { useState, useEffect, useMemo } from "react";
import {
  FaTimes,
  FaInfoCircle,
  FaServer,
  FaMapMarkerAlt,
  FaArrowLeft,
  FaPencilAlt,
} from "react-icons/fa";
import { Alert } from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import InfoIcon from "@mui/icons-material/Info";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoTab from "./Tabs/InfoTab";
import StatsTab from "./Tabs/StatsTab";
import ReportTab from "./Tabs/ReportTab";
import SettingsTab from "./Tabs/SettingsTab";
import MapTab from "./Tabs/MapTab";
import { AppLink } from "../shared";
import useUpdateDeviceData from "../../Hooks/device/useUpdateDeviceData";
import useUpdateDeviceSerialNo from "../../Hooks/device/useUpdateDeviceSerialNo";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";
import useDrawerTransition from "../../Hooks/useDrawerTransition";
import { DEVICE_ACTIVITY_FIELD_LABEL, deviceActivityStateLabel } from "../../utils/deviceActivityLabels";

const DeviceInfoPanel = ({
  open,
  onClose,
  devices,
  setDevices,
  onSelectedDevice = null,
  state,
}) => {
  // Giriş/çıkış kayması — panel kapalı pozisyonda mount olur, iki frame sonra
  // açılır; kapanışta overlay de birlikte söner.
  const { mounted, entered } = useDrawerTransition(open);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedTab, setSelectedTab] = useState("info");
  const [alerts, setAlerts] = useState([]);
  const [showBasicInfo, setShowBasicInfo] = useState(false);
  const { auth } = useAuth();

  // Custom Name edit state
  const [editingCustomName, setEditingCustomName] = useState(false);
  const [customNameText, setCustomNameText] = useState("");
  
  // SerialNo edit state (sadece admin için)
  const [editingSerialNo, setEditingSerialNo] = useState(false);
  const [serialNoText, setSerialNoText] = useState("");
  
  // Admin kontrolü
  const { hasPermission, isAdmin } = usePermission();

  const {
    updateDevice,
    updateDeviceIsLoading,
    updateDeviceIsSuccess,
    updateDeviceIsError,
    updateDeviceError,
  } = useUpdateDeviceData();

  const {
    updateDeviceSerialNo,
    updateDeviceSerialNoIsLoading,
    updateDeviceSerialNoIsSuccess,
    updateDeviceSerialNoIsError,
    updateDeviceSerialNoError,
  } = useUpdateDeviceSerialNo();

  useEffect(() => {
    if (onSelectedDevice) {
      setSelectedDevice(onSelectedDevice);
    }
  }, [onSelectedDevice]);

  useEffect(() => {
    if (devices && selectedDevice) {
      setSelectedDevice(
        devices.find((device) => device.serialNo === selectedDevice.serialNo)
      );
    }
  }, [devices, selectedDevice]);

  // Initialize custom name when selectedDevice changes
  useEffect(() => {
    if (selectedDevice) {
      setCustomNameText(selectedDevice.customName || "");
      setSerialNoText(selectedDevice.serialNo || "");
    }
  }, [selectedDevice]);

  const handleUpdateCustomName = () => {
    if (!selectedDevice) return;
    updateDevice({
      note: selectedDevice.note,
      customName: customNameText,
      token: auth.accessToken,
      id: selectedDevice._id,
    });

    const loadingAlert = {
      id: Date.now(),
      message: "Özel isim güncelleniyor...",
      severity: "info",
    };
    setAlerts((prev) => [...prev, loadingAlert]);
    setTimeout(() => {
      setAlerts((prev) => prev.filter((alert) => alert.id !== loadingAlert.id));
    }, 2000);
  };

  // Success/Error handling for customName update
  useEffect(() => {
    if (updateDeviceIsSuccess) {
      const newAlert = {
        id: Date.now(),
        message: "Özel isim başarıyla güncellendi",
        severity: "success",
      };
      setAlerts((prev) => [...prev, newAlert]);
      setEditingCustomName(false);

      setDevices((prevDevices) =>
        prevDevices.map((device) =>
          device._id === selectedDevice._id
            ? { ...device, customName: customNameText }
            : device
        )
      );

      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== newAlert.id));
      }, 3000);
    }
    if (updateDeviceIsError) {
      const newAlert = {
        id: Date.now(),
        message:
          updateDeviceError?.data?.message ||
          "Özel isim güncellenirken bir hata oluştu",
        severity: "error",
      };
      setAlerts((prev) => [...prev, newAlert]);
      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== newAlert.id));
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDeviceIsSuccess, updateDeviceIsError]);

  const handleUpdateSerialNo = () => {
    if (!selectedDevice) return;
    updateDeviceSerialNo({
      serialNo: serialNoText,
      token: auth.accessToken,
      id: selectedDevice._id,
    });

    const loadingAlert = {
      id: Date.now(),
      message: "Seri numarası güncelleniyor...",
      severity: "info",
    };
    setAlerts((prev) => [...prev, loadingAlert]);
    setTimeout(() => {
      setAlerts((prev) => prev.filter((alert) => alert.id !== loadingAlert.id));
    }, 2000);
  };

  // Success/Error handling for serialNo update
  useEffect(() => {
    if (updateDeviceSerialNoIsSuccess) {
      const newAlert = {
        id: Date.now(),
        message: "Seri numarası başarıyla güncellendi",
        severity: "success",
      };
      setAlerts((prev) => [...prev, newAlert]);
      setEditingSerialNo(false);

      setDevices((prevDevices) =>
        prevDevices.map((device) =>
          device._id === selectedDevice._id
            ? { ...device, serialNo: serialNoText }
            : device
        )
      );

      // selectedDevice'i de güncelle
      setSelectedDevice((prev) => ({
        ...prev,
        serialNo: serialNoText,
      }));

      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== newAlert.id));
      }, 3000);
    }
    if (updateDeviceSerialNoIsError) {
      const newAlert = {
        id: Date.now(),
        message:
          updateDeviceSerialNoError?.data?.message ||
          "Seri numarası güncellenirken bir hata oluştu",
        severity: "error",
      };
      setAlerts((prev) => [...prev, newAlert]);
      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== newAlert.id));
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDeviceSerialNoIsSuccess, updateDeviceSerialNoIsError]);

  const getDeviceImage = (deviceName) => {
    if (!deviceName) return null;

    if (deviceName.includes("Çayset-Fincanda")) {
      return require("../../Assets/icons/caykahvebirarada.png");
    } else if (
      deviceName.includes("Çayset") ||
      deviceName.includes("Çay-Filtre Kahve")
    ) {
      return require("../../Assets/icons/cayfiltremakinesi.png");
    } else if (deviceName.includes("Fincanda")) {
      return require("../../Assets/icons/dortludegirmenli.png");
    } else if (deviceName.includes("Espresso")) {
      return require("../../Assets/icons/espresso_machine.png");
    }
    return null;
  };

  const ALL_PANEL_TABS = useMemo(() => [
    { id: "info", icon: InfoIcon, label: "Bilgi", color: "green", permission: "devices.info" },
    { id: "location", icon: FaMapMarkerAlt, label: "Konum", color: "red", permission: "devices.location" },
    { id: "settings", icon: SettingsIcon, label: "Ayarlar", color: "gray", permission: "devices.settings" },
    { id: "stats", icon: AssessmentIcon, label: "İstatistik", color: "blue", permission: "devices.stats" },
    { id: "report", icon: DescriptionIcon, label: "Rapor", color: "purple", permission: "devices.reports" },
  ], []);

  const visibleTabs = useMemo(() => {
    return ALL_PANEL_TABS.filter((tab) => hasPermission(tab.permission));
  }, [ALL_PANEL_TABS, hasPermission]);

  useEffect(() => {
    setSelectedTab(visibleTabs[0]?.id || "info");
  }, [selectedDevice, visibleTabs]);

  useEffect(() => {
    if (open) {
      const panelContent = document.querySelector(".panel-content");
      if (panelContent) {
        panelContent.scrollTop = 0;
      }
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  if (!selectedDevice) {
    return null;
  }

  return (
    <>
      <div
        className={`fixed inset-y-0 right-0 w-full transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9999] flex flex-col bg-gray-50`}
      >
        {/* Alerts Container */}
        <div className="fixed top-4 right-4 z-[10100] space-y-2">
          {alerts.map((alert) => (
            <Alert
              key={alert.id}
              severity={alert.severity}
              variant="filled"
              className="min-w-[300px] shadow-lg"
              onClose={() =>
                setAlerts((prev) => prev.filter((a) => a.id !== alert.id))
              }
            >
              {typeof alert.message === "string"
                ? alert.message
                : "Bir hata oluştu"}
            </Alert>
          ))}
        </div>

        {/* Panel Başlığı */}
        <div className="flex-none p-4 bg-gradient-to-r from-[#000814] to-[#001D3D] text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FaInfoCircle className="w-6 h-6" />
            <h3 className="text-xl font-semibold">Cihaz Bilgileri</h3>
            {state && (
              <AppLink
                to="/Anasayfa/Firma"
                state={{
                  selectedFirmId: state.fromFirmId,
                  fromFirmName: state.fromFirmName,
                  toPageTab: state.fromPageTab,
                }}
                className=""
              >
                <div className="flex items-center ml-4 gap-2 p-2 rounded-full  text-indigo-800  bg-indigo-200 hover:bg-indigo-300 transition-colors duration-200">
                  <FaArrowLeft className="w-5 h-5 " />
                  <p className="text-base">{state.fromFirmName} Geri Dön</p>
                </div>
              </AppLink>
            )}
          </div>

          <button
            onClick={onClose}
            className="text-white hover:text-indigo-200 p-2 rounded-full hover:bg-indigo-700 transition-colors"
          >
            <FaTimes className="w-6 h-6" />
          </button>
        </div>

        <div className="flex flex-col md:flex-row h-full gap-2 md:gap-6">
          {/* Cihaz Seçimi ve Resim+Bilgiler Bölümü */}
          {window.innerWidth > 768 && (
            <div className="flex-none p-2 md:p-4 bg-gray-50 w-full md:w-auto">
              <div className="flex flex-row md:flex-col items-center  w-full">
                {/* Mobilde sadece resmi göster, tıklanınca modal aç */}
                <div
                  className="relative h-[120px] w-[120px] md:h-[180px] md:w-[180px] flex items-center justify-center bg-gray-50 rounded-lg mb-0 md:mb-4 cursor-pointer md:cursor-default mx-auto"
                  onClick={() =>
                    window.innerWidth < 768 && setShowBasicInfo(!showBasicInfo)
                  }
                >
                  <img
                    src={getDeviceImage(
                      selectedDevice.deviceInfo?.deviceName ||
                        selectedDevice.deviceName
                    )}
                    alt={
                      selectedDevice.deviceInfo?.deviceName ||
                      selectedDevice.deviceName
                    }
                    className=" max-h-[120px] md:max-h-[160px] w-auto object-contain"
                  />
                </div>
                {/* Masaüstünde temel bilgiler, mobilde modal ile açılır */}
                <div className="hidden md:block">
                  <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 max-w-lg w-full mx-auto mt-4 md:mt-0">
                    <div className="flex flex-col items-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-2">
                        <FaServer className="text-indigo-600 w-6 h-6" />
                        Temel Bilgiler
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-base text-gray-500">{DEVICE_ACTIVITY_FIELD_LABEL}</p>
                        <p
                          className={`text-base font-semibold ${
                            selectedDevice.isActive
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {deviceActivityStateLabel(selectedDevice.isActive)}
                        </p>
                      </div>
                      <div>
                        <p className="text-base text-gray-500">Konum</p>
                        <p className="text-base font-semibold">
                          {selectedDevice.deviceStatusName}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-base text-gray-500">Firma Adı</p>
                        <p className="text-base font-semibold">
                          {selectedDevice.firm?.bayserNo} :{" "}
                          {selectedDevice.firm?.firmName}
                        </p>
                      </div>
                      {isAdmin && (
                        <div className="col-span-2">
                          <p className="text-base text-gray-500">Seri No</p>
                          {editingSerialNo ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={serialNoText}
                                onChange={(e) => setSerialNoText(e.target.value)}
                                className="flex-1 p-2 border rounded"
                                placeholder="Seri numarası"
                                disabled={updateDeviceSerialNoIsLoading}
                              />
                              <button
                                onClick={handleUpdateSerialNo}
                                disabled={updateDeviceSerialNoIsLoading}
                                className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                              >
                                {updateDeviceSerialNoIsLoading ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Kaydediliyor...
                                  </>
                                ) : (
                                  "Kaydet"
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setSerialNoText(selectedDevice.serialNo || "");
                                  setEditingSerialNo(false);
                                }}
                                disabled={updateDeviceSerialNoIsLoading}
                                className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                              >
                                İptal
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setEditingSerialNo(true)}
                              className="w-full group flex items-center justify-between px-3 py-2 border rounded-lg bg-white hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                            >
                              <span className="text-base font-semibold text-gray-800">
                                {selectedDevice.serialNo}
                              </span>
                              <FaPencilAlt className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" />
                            </button>
                          )}
                        </div>
                      )}
                      <div className="col-span-2">
                        <p className="text-base text-gray-500">Özel İsim</p>
                        {hasPermission("devices.editName") && editingCustomName ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={customNameText}
                              onChange={(e) =>
                                setCustomNameText(e.target.value)
                              }
                              className="w-full p-2 border rounded"
                              placeholder="Cihaz özel ismi"
                              disabled={updateDeviceIsLoading}
                            />
                            <button
                              onClick={handleUpdateCustomName}
                              disabled={updateDeviceIsLoading}
                              className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                            >
                              {updateDeviceIsLoading ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Kaydediliyor...
                                </>
                              ) : (
                                "Kaydet"
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setCustomNameText(
                                  selectedDevice.customName || ""
                                );
                                setEditingCustomName(false);
                              }}
                              disabled={updateDeviceIsLoading}
                              className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                            >
                              İptal
                            </button>
                          </div>
                        ) : hasPermission("devices.editName") ? (
                          <button
                            onClick={() => setEditingCustomName(true)}
                            className="w-full group flex items-center justify-between px-3 py-2 border rounded-lg bg-white hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                          >
                            <span className="text-base font-semibold text-gray-800">
                              {selectedDevice.customName || "-"}
                            </span>
                            <FaPencilAlt className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" />
                          </button>
                        ) : (
                          <p className="text-base font-semibold text-gray-800 px-3 py-2">
                            {selectedDevice.customName || "-"}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-base text-gray-500">Cihaz Adı</p>
                        <p className="text-base font-semibold">
                          {selectedDevice.deviceInfo?.deviceName ||
                            selectedDevice.deviceName}
                        </p>
                      </div>
                      <div>
                        <p className="text-base text-gray-500">Fincan Sayısı</p>
                        <p className="text-base font-semibold">
                          {selectedDevice.deviceInfo?.cupNumber || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-base text-gray-500">
                          Bağlantı Durumu
                        </p>
                        <p
                          className={`text-base font-semibold ${
                            selectedDevice.deviceInfo?.connection?.status
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {selectedDevice.deviceInfo?.connection?.status
                            ? "Çevrimiçi"
                            : "Çevrimdışı"}
                        </p>
                      </div>
                      <div>
                        <p className="text-base text-gray-500">Son Bağlantı</p>
                        <p className="text-base font-semibold">
                          {selectedDevice.deviceInfo?.connection?.lastConnected
                            ? new Date(
                                selectedDevice.deviceInfo.connection.lastConnected
                              ).toLocaleString("tr-TR")
                            : "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mobilde Temel Bilgiler Modalı */}

          {showBasicInfo && (
            <div className="bg-white rounded-2xl shadow-lg p-4 max-w-lg w-full mx-auto">
              <div className="flex flex-col items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-2">
                  <FaServer className="text-indigo-600 w-6 h-6" />
                  Temel Bilgiler
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-gray-500">{DEVICE_ACTIVITY_FIELD_LABEL}</p>
                  <p
                    className={`text-sm font-semibold ${
                      selectedDevice.isActive
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {deviceActivityStateLabel(selectedDevice.isActive)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Konum</p>
                  <p className="text-sm font-semibold">
                    {selectedDevice.deviceStatusName}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">Firma Adı</p>
                  <p className="text-sm font-semibold">
                    {selectedDevice.firm?.bayserNo} :{" "}
                    {selectedDevice.firm?.firmName}
                  </p>
                </div>
                {isAdmin && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Seri No</p>
                    {editingSerialNo ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={serialNoText}
                          onChange={(e) => setSerialNoText(e.target.value)}
                          className="flex-1 p-2 border rounded"
                          placeholder="Seri numarası"
                          disabled={updateDeviceSerialNoIsLoading}
                        />
                        <button
                          onClick={handleUpdateSerialNo}
                          disabled={updateDeviceSerialNoIsLoading}
                          className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {updateDeviceSerialNoIsLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Kaydediliyor...
                            </>
                          ) : (
                            "Kaydet"
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setSerialNoText(selectedDevice.serialNo || "");
                            setEditingSerialNo(false);
                          }}
                          disabled={updateDeviceSerialNoIsLoading}
                          className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                        >
                          İptal
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingSerialNo(true)}
                        className="w-full group flex items-center justify-between px-3 py-2 border rounded-lg bg-white hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                      >
                        <span className="text-sm font-semibold text-gray-800">
                          {selectedDevice.serialNo}
                        </span>
                        <FaPencilAlt className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" />
                      </button>
                    )}
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">Özel İsim</p>
                  {hasPermission("devices.editName") && editingCustomName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={customNameText}
                        onChange={(e) => setCustomNameText(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Cihaz özel ismi"
                        disabled={updateDeviceIsLoading}
                      />
                      <button
                        onClick={handleUpdateCustomName}
                        disabled={updateDeviceIsLoading}
                        className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {updateDeviceIsLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Kaydediliyor...
                          </>
                        ) : (
                          "Kaydet"
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setCustomNameText(selectedDevice.customName || "");
                          setEditingCustomName(false);
                        }}
                        disabled={updateDeviceIsLoading}
                        className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                      >
                        İptal
                      </button>
                    </div>
                  ) : hasPermission("devices.editName") ? (
                    <button
                      onClick={() => setEditingCustomName(true)}
                      className="w-full group flex items-center justify-between px-3 py-2 border rounded-lg bg-white hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                    >
                      <span className="text-sm font-semibold text-gray-800">
                        {selectedDevice.customName || "-"}
                      </span>
                      <FaPencilAlt className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" />
                    </button>
                  ) : (
                    <p className="text-sm font-semibold text-gray-800 px-3 py-2">
                      {selectedDevice.customName || "-"}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500">Cihaz Adı</p>
                  <p className="text-sm font-semibold">
                    {selectedDevice.deviceInfo?.deviceName ||
                      selectedDevice.deviceName}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Fincan Sayısı</p>
                  <p className="text-sm font-semibold">
                    {selectedDevice.deviceInfo?.cupNumber || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bağlantı Durumu</p>
                  <p
                    className={`text-sm font-semibold ${
                      selectedDevice.deviceInfo?.connection?.status
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {selectedDevice.deviceInfo?.connection?.status
                      ? "Çevrimiçi"
                      : "Çevrimdışı"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Son Bağlantı</p>
                  <p className="text-sm font-semibold">
                    {selectedDevice.deviceInfo?.connection?.lastConnected
                      ? new Date(
                          selectedDevice.deviceInfo.connection.lastConnected
                        ).toLocaleString("tr-TR")
                      : "-"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tab İçerikleri */}
          {!showBasicInfo && (
            <div className="flex-1 z-100 py-1 px-1 md:px-2 overflow-y-auto min-h-0 mt-2 md:mt-4 mr-0 md:mr-2 h-[calc(100vh-110px)] pb-16">
              {selectedTab === "info" && hasPermission("devices.info") && (
                <InfoTab
                  selectedDevice={selectedDevice}
                  setDevices={setDevices}
                  setAlerts={setAlerts}
                />
              )}
              {selectedTab === "stats" && hasPermission("devices.stats") && (
                <StatsTab selectedDevice={selectedDevice} />
              )}
              {selectedTab === "report" && hasPermission("devices.reports") && (
                <ReportTab selectedDevice={selectedDevice} />
              )}
              {selectedTab === "settings" && hasPermission("devices.settings") && (
                <>
                  <SettingsTab
                    setAlerts={setAlerts}
                    selectedDevice={selectedDevice}
                  />
                </>
              )}
              {selectedTab === "location" && hasPermission("devices.location") && (
                <MapTab selectedDevice={selectedDevice} />
              )}
            </div>
          )}
        </div>

        {/* Alt Butonlar */}
        <div className="flex-none z-40 fixed bottom-0 left-0 right-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)] border-t safe-area-bottom">
          <div className="flex justify-between overflow-x-auto">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() =>
                  selectedDevice &&
                  setSelectedTab(selectedTab === tab.id ? (visibleTabs[0]?.id || "info") : tab.id)
                }
                disabled={!selectedDevice}
                className={`flex-1 min-w-[60px] py-3 flex items-center justify-center gap-2 transition-all duration-300 
                  ${!selectedDevice ? "opacity-50 cursor-not-allowed" : ""}
                  ${
                    selectedTab === tab.id && selectedDevice
                      ? `bg-${tab.color}-100 border-t-2 border-${tab.color}-500`
                      : selectedDevice
                      ? `hover:bg-${tab.color}-50`
                      : ""
                  }`}
              >
                <div className="flex flex-col items-center gap-1">
                  {typeof tab.icon === "function" ? (
                    <tab.icon
                      className={`w-5 h-5 md:w-5 md:h-5 ${
                        selectedTab === tab.id && selectedDevice
                          ? `text-${tab.color}-600 transform scale-110`
                          : `text-${tab.color}-500`
                      }`}
                    />
                  ) : (
                    <tab.icon
                      className={`w-5 h-5 md:w-5 md:h-5 ${
                        selectedTab === tab.id && selectedDevice
                          ? `text-${tab.color}-600 transform scale-110`
                          : `text-${tab.color}-500`
                      }`}
                    />
                  )}
                  <span
                    className={`text-xs md:text-sm ${
                      selectedTab === tab.id && selectedDevice
                        ? `text-${tab.color}-600 font-medium`
                        : `text-${tab.color}-500`
                    }`}
                  >
                    {tab.label}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overlay */}
      {mounted && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 md:bg-opacity-20 z-[9998] pointer-events-auto transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
          onClick={onClose}
        />
      )}
    </>
  );
};

export default DeviceInfoPanel;
